import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Crown, Clock, CalendarDays, Coins, BadgeCheck, ShieldCheck, AlertTriangle,
  CheckCircle2, ArrowRight, Send, Loader2, Trophy, Users,
} from "lucide-react";
import { api } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";
import { Modal, Field, TextInput, TextArea, PrimaryButton } from "@/components/admin/ui";

const highlights = [
  { Icon: CalendarDays, label: "Policy Period", value: "30 Days" },
  { Icon: Clock, label: "Monthly Live", value: "120 Hours" },
  { Icon: Trophy, label: "Daily Target", value: "4 Hours" },
  { Icon: Coins, label: "Daily Reward", value: "5,000 Coins" },
];

const eligibility = [
  "Must have an active Golden Live account with a valid User ID.",
  "Minimum age of 18 years.",
  "Own a smartphone with a stable internet connection.",
  "Able to go Live consistently every day during the policy period.",
  "No active ban or serious violation history on Golden Live.",
];

const salaryConditions = [
  "Complete a minimum of 120 Live hours within the 30-day policy period.",
  "Maintain an average of at least 4 hours of Live per day.",
  "Salary is paid by the 15th of each month for the previous period.",
  "Daily reward of 5,000 Coins is granted for completing 2 hours of Live in a single day.",
  "Incomplete monthly hours may result in reduced or withheld salary as per the tier policy.",
];

const performance = [
  "Go Live daily and follow your assigned schedule.",
  "Keep audience engagement high with quality, interactive content.",
  "Respond to agency and admin guidance in a timely manner.",
  "Meet or exceed the monthly gifting/activity targets set by your agency.",
];

const rules = [
  "No nudity, violence, hate speech or illegal content during Live.",
  "No fraud, fake gifting, or manipulation of Live hours or rewards.",
  "No promotion of other live-streaming platforms while on Golden Live.",
  "Respect all viewers, hosts and staff — harassment is strictly prohibited.",
  "Repeated violations may lead to salary forfeiture and permanent removal.",
];

const emptyForm = {
  full_name: "", user_id: "", whatsapp: "", age: "", country: "", city: "",
  agency_name: "", live_experience: "", host_experience: "", avg_daily_hours: "",
  reason: "", preferred_contact: "WhatsApp", additional_info: "",
};

function TermList({ items, testid }) {
  return (
    <ul className="mt-4 space-y-3" data-testid={testid}>
      {items.map((t, i) => (
        <li key={i} className="flex gap-3 text-sm leading-relaxed text-slate-300">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function Section({ title, Icon, children }) {
  return (
    <div className="card-surface rounded-2xl p-6 sm:p-8">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/10">
          <Icon className="h-5 w-5 text-gold" />
        </span>
        <h2 className="font-display text-xl font-bold text-white sm:text-2xl">{title}</h2>
      </div>
      {children}
    </div>
  );
}

export default function OfficialHost() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = useMutation({
    mutationFn: async () => api.post("/host-applications", form),
    onSuccess: () => {
      toast.success("Application submitted! Our team will contact you soon.");
      setForm(emptyForm);
      setOpen(false);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to submit. Please try again."),
  });

  const ApplyButton = ({ testid }) => (
    <button
      onClick={() => setOpen(true)}
      data-testid={testid}
      className="inline-flex h-12 items-center justify-center gap-2 rounded-full gold-gradient px-8 py-3.5 text-base font-bold text-obsidian transition-transform hover:scale-[1.03]"
    >
      <Crown className="h-5 w-5" /> Apply as Official Host <ArrowRight className="h-5 w-5" />
    </button>
  );

  return (
    <div>
      {/* Hero */}
      <section className="hero-radial">
        <div className="mx-auto max-w-5xl px-4 py-14 text-center sm:px-6 sm:py-20 lg:px-12">
          <span className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/5 px-4 py-1.5 text-xs font-semibold text-gold">
            <Crown className="h-3.5 w-3.5" /> Become a Golden Live Star
          </span>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl lg:text-6xl">
            Official Host <span className="gold-text">Salary & Rewards Policy</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-slate-300">
            Go Live, grow your audience, and earn a monthly salary plus daily coin rewards as an
            Official Golden Live Host. Here is everything you need to know.
          </p>
          <div className="mt-8 flex justify-center">
            <ApplyButton testid="host-apply-hero-btn" />
          </div>

          <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {highlights.map(({ Icon, label, value }) => (
              <div key={label} className="card-surface rounded-2xl p-5 text-center">
                <Icon className="mx-auto h-6 w-6 text-gold" />
                <p className="mt-3 font-display text-2xl font-bold text-white">{value}</p>
                <p className="mt-1 text-xs uppercase tracking-wider text-slate-400">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Policy body */}
      <section className="mx-auto max-w-5xl space-y-6 px-4 pb-10 sm:px-6 lg:px-12">
        <div className="card-surface rounded-2xl border-gold/25 p-6 sm:p-8">
          <div className="flex items-center gap-3 text-gold">
            <Coins className="h-6 w-6" />
            <h2 className="font-display text-xl font-bold sm:text-2xl">Salary & Reward Summary</h2>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <p className="flex items-start gap-3 rounded-xl bg-obsidian p-4 text-sm text-slate-300">
              <CalendarDays className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              Salary is paid <b className="text-white">&nbsp;by the 15th&nbsp;</b> of every month for the completed policy period.
            </p>
            <p className="flex items-start gap-3 rounded-xl bg-obsidian p-4 text-sm text-slate-300">
              <Coins className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              Earn <b className="text-white">&nbsp;5,000 Coins&nbsp;</b> daily for completing 2 hours of Live in a single day.
            </p>
            <p className="flex items-start gap-3 rounded-xl bg-obsidian p-4 text-sm text-slate-300">
              <Clock className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              Target <b className="text-white">&nbsp;120 Live hours&nbsp;</b> per 30-day period (≈ 4 hours/day).
            </p>
            <p className="flex items-start gap-3 rounded-xl bg-obsidian p-4 text-sm text-slate-300">
              <Trophy className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              Consistent top performers unlock <b className="text-white">&nbsp;higher salary tiers&nbsp;</b> and bonuses.
            </p>
          </div>
        </div>

        <Section title="Host Eligibility" Icon={Users}>
          <TermList items={eligibility} testid="host-eligibility-list" />
        </Section>

        <Section title="Salary Eligibility Conditions" Icon={BadgeCheck}>
          <TermList items={salaryConditions} testid="host-salary-list" />
        </Section>

        <Section title="Performance & Activity Requirements" Icon={ShieldCheck}>
          <TermList items={performance} testid="host-performance-list" />
        </Section>

        <div className="card-surface rounded-2xl border-amber-500/30 p-6 sm:p-8">
          <div className="flex items-center gap-3 text-amber-400">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/10">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <h2 className="font-display text-xl font-bold text-white sm:text-2xl">Host Rules & Violations</h2>
          </div>
          <TermList items={rules} testid="host-rules-list" />
        </div>

        <div className="card-surface rounded-2xl p-6 text-sm leading-relaxed text-slate-400 sm:p-8">
          <h3 className="mb-2 font-bold text-slate-200">Other Terms & Conditions</h3>
          Golden Live reserves the right to update this policy, salary tiers, and reward amounts at any time.
          Final decisions on salary, rewards, approvals and violations rest solely with Golden Live management.
          Hours and rewards are calculated based on official in-app records. By applying, you agree to these terms.
        </div>

        {/* CTA */}
        <div className="rounded-2xl gold-border bg-gradient-to-br from-gold/10 to-transparent p-8 text-center">
          <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">Ready to go Live and earn?</h2>
          <p className="mx-auto mt-2 max-w-xl text-slate-300">
            Apply now and our team will review your application and reach out with the next steps.
          </p>
          <div className="mt-6 flex justify-center">
            <ApplyButton testid="host-apply-cta-btn" />
          </div>
        </div>
      </section>

      {/* Application form */}
      <Modal open={open} onClose={() => setOpen(false)} title="Official Host Application" testid="host-application-modal">
        <form onSubmit={(e) => { e.preventDefault(); submit.mutate(); }} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Full Name *">
              <TextInput value={form.full_name} onChange={set("full_name")} required testid="host-fullname-input" />
            </Field>
            <Field label="Golden Live User ID *">
              <TextInput value={form.user_id} onChange={set("user_id")} required testid="host-userid-input" />
            </Field>
            <Field label="Mobile / WhatsApp Number *">
              <TextInput value={form.whatsapp} onChange={set("whatsapp")} required placeholder="e.g. 8801XXXXXXXXX" testid="host-whatsapp-input" />
            </Field>
            <Field label="Age">
              <TextInput type="number" value={form.age} onChange={set("age")} testid="host-age-input" />
            </Field>
            <Field label="Country">
              <input list="host-country-list" value={form.country} onChange={set("country")} data-testid="host-country-input"
                className="h-11 w-full rounded-lg border border-gold/20 bg-obsidian px-3 text-slate-100 outline-none focus:border-gold/60" />
              <datalist id="host-country-list">{COUNTRIES.map((c) => <option key={c.name} value={c.name} />)}</datalist>
            </Field>
            <Field label="City">
              <TextInput value={form.city} onChange={set("city")} testid="host-city-input" />
            </Field>
            <Field label="Current Agency Name (if any)">
              <TextInput value={form.agency_name} onChange={set("agency_name")} testid="host-agency-input" />
            </Field>
            <Field label="Average Daily Live Hours">
              <TextInput value={form.avg_daily_hours} onChange={set("avg_daily_hours")} placeholder="e.g. 4" testid="host-hours-input" />
            </Field>
          </div>

          <Field label="Previous Live App Experience">
            <TextInput value={form.live_experience} onChange={set("live_experience")} placeholder="Which apps? How long?" testid="host-liveexp-input" />
          </Field>
          <Field label="Previous Host Experience">
            <TextInput value={form.host_experience} onChange={set("host_experience")} placeholder="Any hosting/agency experience" testid="host-hostexp-input" />
          </Field>
          <Field label="Why do you want to become an Official Host?">
            <TextArea value={form.reason} onChange={set("reason")} testid="host-reason-input" />
          </Field>
          <Field label="Preferred Contact Method">
            <select value={form.preferred_contact} onChange={set("preferred_contact")} data-testid="host-contact-input"
              className="h-11 w-full rounded-lg border border-gold/20 bg-obsidian px-3 text-slate-100 outline-none focus:border-gold/60">
              <option>WhatsApp</option>
              <option>Phone Call</option>
              <option>Telegram</option>
              <option>In-App Message</option>
            </select>
          </Field>
          <Field label="Any additional information">
            <TextArea value={form.additional_info} onChange={set("additional_info")} testid="host-additional-input" />
          </Field>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="rounded-lg px-5 py-2.5 text-sm font-semibold text-slate-300 hover:bg-white/5">Cancel</button>
            <PrimaryButton type="submit" disabled={submit.isPending} testid="host-submit-btn">
              {submit.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              {submit.isPending ? "Submitting..." : "Submit Application"}
            </PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
